import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, CheckCircle, Heart, MapPin, Phone, Mail, Clock } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { clinicInfo, services, reviews, aboutContent } from '../data/mockData';

const Home = () => {
  console.log('Home component rendering. Map URL:', clinicInfo.mapEmbedUrl);
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1733783489145-f3d3ee7a9ccf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHx2ZXRlcmluYXJpYW4lMjBkb2clMjBjYXR8ZW58MHx8fHwxNzcxMzIzOTgzfDA&ixlib=rb-4.1.0&q=85"
            alt="Veteriner kliniği"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-emerald-800/85 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
              <span>{clinicInfo.rating} Yıldız • {clinicInfo.totalReviews} Değerlendirme</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Evcil Dostlarınız İçin En İyi Bakım
            </h1>

            <p className="text-xl text-emerald-50 mb-8 leading-relaxed">
              1990'dan beri Kocaeli'nde güvenilir veteriner hizmeti. Modern ekipman, deneyimli kadro ve sevgi dolu yaklaşım.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/randevu"
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold inline-flex items-center gap-2 transition-all shadow-lg hover:shadow-xl"
              >
                Randevu Al
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href={`tel:${clinicInfo.phone}`}
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all"
              >
                Hemen Ara
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutContent.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-emerald-800 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Hizmetlerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Evcil dostlarınızın sağlığı için kapsamlı veteriner hizmetleri sunuyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-xl">
                      {IconComponent && <IconComponent className="h-6 w-6 text-emerald-700" />}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-emerald-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/hizmetler"
              className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-semibold text-lg"
            >
              Tüm Hizmetleri Görün
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1654895716780-b4664497420d"
                alt="Uzman veteriner ekibi"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-emerald-900 mb-6">
                Neden Bizi Seçmelisiniz?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {aboutContent.history}
              </p>
              <div className="space-y-4">
                {[
                  'Deneyimli ve uzman veteriner kadrosu',
                  'Modern tıbbi ekipman ve teknoloji',
                  'Her hayvan için özel tedavi planları',
                  '7/24 acil müdahale hizmeti',
                  '%95+ hasta memnuniyet oranı',
                  'Uygun fiyat ve ödeme seçenekleri'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/hakkimizda"
                className="inline-flex items-center gap-2 mt-8 text-emerald-700 hover:text-emerald-800 font-semibold text-lg"
              >
                Daha Fazla Bilgi
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Map & Contact Section */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Contact Info */}
            <div>
              <h2 className="text-4xl font-bold text-emerald-900 mb-6">
                İletişim & Ulaşım
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Kliniğimize ulaşım çok kolay. İzmit merkezde, kolay tarif edilebilir bir konumdayız.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-emerald-100 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Adres</h3>
                    <p className="text-gray-600">{clinicInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Telefon</h3>
                    <a href={`tel:${clinicInfo.phone}`} className="text-gray-600 hover:text-emerald-700 transition-colors">
                      {clinicInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-blue-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">E-posta</h3>
                    <a href={`mailto:${clinicInfo.email}`} className="text-gray-600 hover:text-emerald-700 transition-colors">
                      {clinicInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Çalışma Saatleri</h3>
                    <p className="text-gray-600">{clinicInfo.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-[500px] w-full bg-white p-4 rounded-2xl shadow-xl" style={{ height: '500px' }}>
              <iframe
                src={clinicInfo.mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '0.75rem' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Clinic Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Müşterilerimiz Ne Diyor?
            </h2>
            <div className="flex items-center justify-center gap-2 text-lg">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-amber-500 text-amber-500" />
                ))}
              </div>
              <span className="text-gray-600">
                {clinicInfo.rating} • {clinicInfo.totalReviews} değerlendirme
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.slice(0, 6).map((review) => (
              <div
                key={review.id}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-amber-600 flex items-center justify-center text-white font-semibold">
                    {review.avatar}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-500">{review.date}</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed">{review.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-800 via-emerald-700 to-amber-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="h-16 w-16 text-white mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-6">
            Evcil Dostunuz İçin Hemen Randevu Alın
          </h2>
          <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Uzman veterinerlerimizle tanışın ve evcil dostlarınıza en iyi bakımı sağlayın
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/randevu"
              className="bg-white text-emerald-800 px-8 py-4 rounded-lg font-semibold inline-flex items-center gap-2 hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              Online Randevu Al
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href={`tel:${clinicInfo.phone}`}
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
            >
              {clinicInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
